/**
 * Home Hub API (services/agent/app/home_routes.py): the signed-in person's week and
 * their Quick links saved per account. DWA users only. KB home-hub.md.
 */
import { authService, authFetch } from './auth';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export interface MyWorkDay {
  date: string;
  planned_hours: number;
  task_count: number;
  logged_hours: number;
}

export interface MyWorkTask {
  id: number | string | null;
  name: string;
  project: string | null;
  due_date: string | null;
  overdue: boolean;
  url: string | null;
}

export interface MyWorkDeal {
  id: string;
  name: string;
  stage: string | null;
  amount: number | null;
  close_date: string | null;
  /** When the deal entered its current stage (for "stalled" tips). */
  stage_entered_at?: string | null;
}

export interface MyWork {
  week_start: string;
  week_end: string;
  /** Null when the signed-in email isn't a Teamwork person. */
  teamwork: null | {
    name: string;
    target_hours: number;
    planned_hours: number;
    planned_tasks: number;
    logged_hours: number;
    billable_hours: number;
    planned_utilization: number;
    logged_utilization: number;
    days: MyWorkDay[];
    snapshot_at: string | null;
  };
  tasks: null | { status: 'ok' | 'unavailable'; items: MyWorkTask[] };
  /** Null when the signed-in email isn't a HubSpot owner. */
  hubspot: null | {
    open_count: number;
    open_amount: number;
    past_due_count: number;
    items: MyWorkDeal[];
    synced_at: string | null;
  };
}

export interface PinsResponse {
  item_ids: string[];
  /** False until this account has saved pins (the browser's old pins get uploaded then). */
  stored: boolean;
}

async function request<T>(method: 'GET' | 'PUT', path: string, body?: unknown): Promise<T> {
  const token = authService.getAccessToken();
  const resp = await authFetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const payload = await resp.json();
      if (typeof payload.detail === 'string') detail = payload.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return resp.json() as Promise<T>;
}

export const fetchMyWork = () => request<MyWork>('GET', '/home/my-work');
export const fetchPins = () => request<PinsResponse>('GET', '/home/pins');
export const savePins = (itemIds: string[]) => request<PinsResponse>('PUT', '/home/pins', { item_ids: itemIds });
